import { isFunction, isString } from 'lodash';
import axios, { AxiosError, AxiosResponse, AxiosRequestConfig, isAxiosError } from 'axios';
import { Dispatch, Middleware, createAction, Action, PayloadAction, MiddlewareAPI } from '@reduxjs/toolkit';
import { getAccessToken as retrieveAccessToken } from '@oliasoft/pkce';
import ClientConfig from 'src/config/config';
import { toast } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import i18n from 'src/internationalisation/i18n';

axios.interceptors.request.use((config) => {
  const company = JSON.parse(sessionStorage.getItem('activeCompany') as string);

  return {
    ...config,
    params: {
      ...config.params,
      companyId: company?.id,
    },
  };
});

type GeneralCallback = (payload: any) => any;

type PayloadActionCreatorCallback = (payload: any) => PayloadAction<any>;

type APICallbackActionType = string | Action | PayloadActionCreatorCallback | GeneralCallback;

export type APIActionPayload = AxiosRequestConfig & {
  onStart?: APICallbackActionType;
  onSuccess?: APICallbackActionType;
  onError?: APICallbackActionType;
};

export interface ApiErrorPayload {
  message?: string;
  code?: string;
  status?: number;
  errorMessages?: string | string[];
  [key: string]: unknown;
}

export const apiCallBegan = createAction<APIActionPayload>('api/CallBegan');

export const apiCallSuccess = createAction<AxiosResponse>('api/CallSuccess');

export const apiCallFailed = createAction<AxiosError | unknown>('api/CallFailed');

export const dispatchAPICallback = (dispatch: Dispatch, action?: APICallbackActionType, payload?: unknown) => {
  if (action) {
    if (isFunction(action)) {
      // invoke the callback function
      const result = action(payload);
      // if the function was an action creator, the result will have an action type, so dispatch it
      if (result?.type) {
        dispatch(result);
      }
    } else if (isString(action)) {
      dispatch({ type: action, payload });
    }
  }
};

export const getAccessToken = () => {
  if (!ClientConfig.config.pkce.provider) {
    return 'NO_PKCE_ACCESS_TOKEN_YET';
  }
  const accessToken = retrieveAccessToken(
    ClientConfig.config.pkce.provider,
    ClientConfig.config.applicationId,
    ClientConfig.config.idpServerUrl,
  );
  return accessToken;
};

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: translations.httpErrors_badRequest,
  401: translations.httpErrors_unauthorized,
  403: translations.httpErrors_forbidden,
  404: translations.httpErrors_notFound,
  408: translations.httpErrors_requestTimeout,
  409: translations.httpErrors_conflict,
  422: translations.httpErrors_unprocessableEntity,
  429: translations.httpErrors_tooManyRequests,
  500: translations.httpErrors_internalServerError,
  502: translations.httpErrors_badGateway,
  503: translations.httpErrors_serviceUnavailable,
  504: translations.httpErrors_gatewayTimeout,
};

export const getHttpErrorMessage = (error: ApiErrorPayload): string => {
  const backendMessage = extractBackendMessage(error);
  if (backendMessage) return backendMessage;

  if (error.status && HTTP_STATUS_MESSAGES[error.status]) {
    return i18n.t(HTTP_STATUS_MESSAGES[error.status]);
  }
  if (error.code === 'ERR_NETWORK') {
    return i18n.t(translations.httpErrors_networkError);
  }
  if (error.code === 'ECONNABORTED') {
    return i18n.t(translations.httpErrors_connectionAborted);
  }

  //fallback
  return error.message ?? i18n.t(translations.httpErrors_unexpectedError);
};

const extractBackendMessage = (error: ApiErrorPayload): string | null => {
  if (!error.errorMessages) return null;

  if (Array.isArray(error.errorMessages)) {
    return error.errorMessages.length > 0 ? error.errorMessages.join('. ') : null;
  }

  return error.errorMessages;
};

const api: Middleware =
  ({ dispatch }: MiddlewareAPI) =>
  (next) =>
  async (action) => {
    if (!apiCallBegan.match(action)) {
      return next(action);
    }

    const { baseURL, headers, onStart, onSuccess, onError }: APIActionPayload = action.payload;

    if (onStart) {
      dispatchAPICallback(dispatch, onStart);
    }
    next(action);

    try {
      const response = await axios.request({
        ...action.payload,
        baseURL: baseURL ?? ClientConfig.config.baseApiUrl,
        headers: {
          ...headers,
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      dispatch(apiCallSuccess(response.data));
      dispatchAPICallback(dispatch, onSuccess, response.data);
    } catch (error) {
      // Axios errors are not serializable so don't send full error as action payload
      const backendError = isAxiosError(error) ? (error?.response?.data ?? {}) : {};
      const errorPayload = isAxiosError(error)
        ? {
            ...backendError,
            message: error?.message,
            code: error?.code,
            status: error?.response?.status,
            errorMessages: backendError?.errorMessages || backendError?.message,
          }
        : { message: 'An API error occurred' };
      dispatch(apiCallFailed(errorPayload));

      if (onError) {
        dispatchAPICallback(dispatch, onError, errorPayload);
      } else {
        //default toast for unhandled API errors
        toast({
          message: {
            type: 'Error',
            content: getHttpErrorMessage(errorPayload),
          },
        });
      }
    }
  };

export default api;
